import { BaseHTMLElement, customElement, first, onDoc, onEvent } from 'dom-native';

import { escapeHtml } from '@/services/markdown';
import { getImageUrl } from '@/services/image-url';
import type { GalleryState } from '@/types/chat';
import { icon } from '@/utils/icons';

@customElement('help-agent-image-gallery-modal')
export class HelpAgentImageGalleryModal extends BaseHTMLElement {
  private _gallery: GalleryState | null = null;

  init(): void {
    super.init();
    this.render();
  }

  setState(gallery: GalleryState | null): void {
    this._gallery = gallery;
    if (this.initialized) {
      this.render();
    }
  }

  private render(): void {
    if (!this._gallery || this._gallery.images.length === 0) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    const currentImage = this._gallery.images[this._gallery.currentIndex];
    const caption = currentImage.step_number
      ? `Step ${currentImage.step_number}: ${currentImage.caption || ''}`.trim()
      : (currentImage.caption || '');
    const escapedCaption = caption ? escapeHtml(caption) : '';
    const escapedImageUrl = escapeHtml(getImageUrl(currentImage.url, this._getAiHelperApiBaseUrl()));

    this.hidden = false;
    this.innerHTML = `
      <div class="help-agent-gallery-overlay" data-test="help-widget-gallery-overlay">
        <div
          class="help-agent-gallery-content"
          data-test="help-widget-gallery-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery"
        >
          <button
            type="button"
            class="help-agent-gallery-close"
            data-action="close"
            data-test="help-widget-gallery-close"
            aria-label="Close gallery"
          >
            ${icon('close', { size: 16 })}
          </button>
          <div class="help-agent-gallery-stage">
            <button
              type="button"
              class="help-agent-gallery-nav"
              data-action="prev"
              data-test="help-widget-gallery-prev"
              aria-label="Previous image"
              ${this._gallery.images.length <= 1 ? 'hidden' : ''}
            >
              ${icon('chevron-left', { size: 18 })}
            </button>
            <figure class="help-agent-gallery-figure" data-test="help-widget-gallery-figure">
              <img
                class="help-agent-gallery-image"
                data-test="help-widget-gallery-image"
                src="${escapedImageUrl}"
                alt="${escapedCaption || 'Reference image'}"
              />
              ${escapedCaption ? `<figcaption class="help-agent-gallery-caption">${escapedCaption}</figcaption>` : ''}
            </figure>
            <button
              type="button"
              class="help-agent-gallery-nav"
              data-action="next"
              data-test="help-widget-gallery-next"
              aria-label="Next image"
              ${this._gallery.images.length <= 1 ? 'hidden' : ''}
            >
              ${icon('chevron-right', { size: 18 })}
            </button>
          </div>
          <div class="help-agent-gallery-counter" data-test="help-widget-gallery-counter">
            ${this._gallery.currentIndex + 1} / ${this._gallery.images.length}
          </div>
        </div>
      </div>
    `;
  }

  private _getAiHelperApiBaseUrl(): string | null {
    const widget = this.closest('help-agent-app') as { config?: { apiUrl?: string | null } } | null;
    return widget?.config?.apiUrl ?? null;
  }

  private emit(name: string): void {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  }

  @onEvent('click')
  onOverlayClick(event: MouseEvent): void {
    const overlay = first(this, '.help-agent-gallery-overlay');
    if (overlay && event.target === overlay) {
      this.emit('help-agent:gallery-close');
    }
  }

  @onEvent('click', '[data-action="close"]')
  onClose(): void {
    this.emit('help-agent:gallery-close');
  }

  @onEvent('click', '[data-action="prev"]')
  onPrev(): void {
    this.emit('help-agent:gallery-prev');
  }

  @onEvent('click', '[data-action="next"]')
  onNext(): void {
    this.emit('help-agent:gallery-next');
  }

  @onDoc('keydown')
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (!this._gallery) {
      return;
    }

    if (event.key === 'Escape') {
      this.emit('help-agent:gallery-close');
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.emit('help-agent:gallery-prev');
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.emit('help-agent:gallery-next');
    }
  }
}
